<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

import AgentAvailabilityAPI from 'dashboard/api/agentAvailability';
import TeamsAPI from 'dashboard/api/teams';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const teamId = computed(() => route.params.teamId);
const members = ref([]);
const managerIds = ref([]);
const isLoading = ref(true);

const isManager = agentId => managerIds.value.includes(agentId);

const load = async () => {
  const [{ data: agents }, { data: managers }] = await Promise.all([
    TeamsAPI.getAgents({ teamId: teamId.value }),
    AgentAvailabilityAPI.getTeamManagers(teamId.value),
  ]);
  members.value = agents;
  managerIds.value = managers.map(manager => manager.user_id);
};

onMounted(async () => {
  try {
    await load();
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.MANAGERS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});

const toggleManager = async agent => {
  try {
    if (isManager(agent.id)) {
      await AgentAvailabilityAPI.removeTeamManager(teamId.value, agent.id);
    } else {
      await AgentAvailabilityAPI.addTeamManager(teamId.value, agent.id);
    }
    await load();
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.MANAGERS.API.ERROR'));
  }
};

const goToNextStep = () => {
  store.dispatch('teams/get');
};
</script>

<template>
  <div class="h-full w-full p-8 col-span-6 overflow-y-auto">
    <Spinner v-if="isLoading" />
    <template v-else>
      <h2 class="text-heading-2 text-n-slate-12">
        {{ $t('TEAMS_SETTINGS.MANAGERS.TITLE') }}
      </h2>
      <p class="text-body-main text-n-slate-11 mb-6">
        {{ $t('TEAMS_SETTINGS.MANAGERS.DESC') }}
      </p>

      <p v-if="!members.length" class="text-body-main text-n-slate-11">
        {{ $t('TEAMS_SETTINGS.MANAGERS.NO_MEMBERS') }}
      </p>
      <table
        v-else
        class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
      >
        <tbody class="divide-y divide-n-weak">
          <tr v-for="agent in members" :key="agent.id">
            <td class="py-3 ltr:pl-4 rtl:pr-4">
              <span class="text-body-main text-n-slate-12">{{
                agent.name
              }}</span>
              <span class="text-body-main text-n-slate-11 ltr:ml-2 rtl:mr-2">
                {{ agent.email }}
              </span>
            </td>
            <td class="py-3 ltr:pr-4 rtl:pl-4 text-end">
              <NextButton
                :label="
                  isManager(agent.id)
                    ? $t('TEAMS_SETTINGS.MANAGERS.REMOVE')
                    : $t('TEAMS_SETTINGS.MANAGERS.ADD')
                "
                :slate="!isManager(agent.id)"
                sm
                @click="toggleManager(agent)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="flex justify-end py-4">
        <NextButton
          :label="$t('TEAMS_SETTINGS.MANAGERS.DONE')"
          @click="goToNextStep"
        />
      </div>
    </template>
  </div>
</template>
